import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import { getFirestoreInstance } from './firestore';
import type { Person } from '@gigdriver/domain';
import { fromFirestore } from './serde';

function peopleCollection(orgId: string) {
  return collection(getFirestoreInstance(), 'organizations', orgId, 'people');
}

export async function createPerson(
  orgId: string,
  person: Omit<Person, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const personRef = doc(peopleCollection(orgId));
  const personId = personRef.id;

  await setDoc(personRef, {
    ...person,
    id: personId,
    orgId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return personId;
}

export async function getPerson(orgId: string, personId: string): Promise<Person | null> {
  const personRef = doc(getFirestoreInstance(), 'organizations', orgId, 'people', personId);
  const docSnap = await getDoc(personRef);
  return docSnap.exists() ? fromFirestore<Person>(docSnap.data()) : null;
}

export async function getOrgPeople(orgId: string): Promise<Person[]> {
  const snapshot = await getDocs(peopleCollection(orgId));
  return snapshot.docs.map(d => fromFirestore<Person>(d.data()));
}

// This org's roster record linked to the given auth uid, if any — lets a
// member (e.g. `staff`, who can't otherwise read the roster) resolve their
// own personId for self-service actions like "request to fill".
export async function getMyPerson(orgId: string, userId: string): Promise<Person | null> {
  const q = query(peopleCollection(orgId), where('userId', '==', userId), limit(1));
  const snapshot = await getDocs(q);
  return snapshot.empty ? null : fromFirestore<Person>(snapshot.docs[0].data());
}

export async function updatePerson(
  orgId: string,
  personId: string,
  updates: Partial<Person>,
): Promise<void> {
  const personRef = doc(getFirestoreInstance(), 'organizations', orgId, 'people', personId);
  await updateDoc(personRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePerson(orgId: string, personId: string): Promise<void> {
  const personRef = doc(getFirestoreInstance(), 'organizations', orgId, 'people', personId);
  await deleteDoc(personRef);
}
